import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { StarIcon, MapPinIcon } from '@heroicons/react/24/outline';

import api from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const VendorStore = () => {
  const { slug } = useParams();

  // جلب بيانات المتجر
  const { data: storeData, isLoading } = useQuery(
    ['vendor-store', slug],
    () => api.get(`/vendors/store/${slug}`).then(res => res.data.data)
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!storeData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            المتجر غير موجود
          </h2>
          <Link to="/vendors" className="btn-primary">
            العودة للمتاجر
          </Link>
        </div>
      </div>
    );
  }

  const { vendor, products } = storeData;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Store Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-6 md:space-x-reverse">
            {/* Store Logo */}
            <div className="flex-shrink-0">
              {vendor.logo_url ? (
                <img
                  src={vendor.logo_url}
                  alt={vendor.store_name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                />
              ) : (
                <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
                  <span className="text-primary-600 font-bold text-2xl">
                    {vendor.store_name?.charAt(0) || 'م'}
                  </span>
                </div>
              )}
            </div>

            {/* Store Info */}
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {vendor.store_name}
              </h1>
              
              <div className="flex items-center space-x-4 space-x-reverse mb-3">
                <span className="inline-flex px-3 py-1 text-sm font-medium bg-primary-100 text-primary-800 rounded-full">
                  {vendor.category}
                </span>
                
                {vendor.rating_count > 0 && (
                  <div className="flex items-center">
                    <StarIcon className="w-5 h-5 text-yellow-400 fill-current ml-1" />
                    <span className="text-sm font-medium text-gray-900">
                      {vendor.rating_average ? parseFloat(vendor.rating_average).toFixed(1) : '0.0'}
                    </span>
                    <span className="text-sm text-gray-500 mr-1">
                      ({vendor.rating_count} تقييم)
                    </span>
                  </div>
                )}
              </div>

              {vendor.description && (
                <p className="text-gray-600 mb-4 max-w-2xl">
                  {vendor.description}
                </p>
              )}

              {/* Social Media Links */}
              {vendor.social_media && (
                <div className="flex space-x-4 space-x-reverse">
                  {vendor.social_media.whatsapp && (
                    <a
                      href={`https://wa.me/${vendor.social_media.whatsapp}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:text-green-700"
                    >
                      واتساب
                    </a>
                  )}
                  {vendor.social_media.facebook && (
                    <a
                      href={vendor.social_media.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700"
                    >
                      فيسبوك
                    </a>
                  )}
                  {vendor.social_media.instagram && (
                    <a
                      href={vendor.social_media.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-pink-600 hover:text-pink-700"
                    >
                      إنستغرام
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Store Products */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            منتجات المتجر
          </h2>
          <Link
            to={`/store/${vendor.store_slug}/products`}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            عرض جميع المنتجات ←
          </Link>
        </div>

        {products && products.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <div key={product.id} className="product-card">
                <Link to={`/products/${product.id}`}>
                  <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-t-lg bg-gray-200">
                    <img
                      src={product.images?.[0]?.url || '/placeholder-product.jpg'}
                      alt={product.name}
                      className="h-48 w-full object-cover object-center hover:opacity-75 transition-opacity"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-2 line-clamp-2">
                      {product.name}
                    </h3>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-lg font-bold text-primary-600">
                        {product.price ? parseFloat(product.price).toLocaleString() : '0'} جنيه
                      </p>
                      {product.compare_price && (
                        <p className="text-sm text-gray-500 line-through">
                          {parseFloat(product.compare_price).toLocaleString()} جنيه
                        </p>
                      )}
                    </div>
                    {product.category && (
                      <p className="text-xs text-gray-500 mb-2">
                        {product.category.name}
                      </p>
                    )}
                    {product.rating_average > 0 && (
                      <div className="flex items-center">
                        <StarIcon className="w-4 h-4 text-yellow-400 fill-current ml-1" />
                        <span className="text-sm text-gray-600">
                          {parseFloat(product.rating_average).toFixed(1)}
                        </span>
                        <span className="text-xs text-gray-500 mr-1">
                          ({product.rating_count || 0})
                        </span>
                      </div>
                    )}
                  </div>
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-8l-4 4m0 0l-4-4m4 4V3" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              لا توجد منتجات
            </h3>
            <p className="text-gray-500">
              لم يقم هذا المتجر بإضافة منتجات بعد
            </p>
          </div>
        )}
      </div>

      {/* Store Stats */}
      <div className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-2xl font-bold text-primary-600">
                {products?.length || 0}
              </div>
              <div className="text-sm text-gray-600">منتج</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary-600">
                {vendor.rating_average ? parseFloat(vendor.rating_average).toFixed(1) : '0.0'}
              </div>
              <div className="text-sm text-gray-600">تقييم المتجر</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary-600">
                {vendor.rating_count || 0}
              </div>
              <div className="text-sm text-gray-600">تقييم</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorStore;